<template>
  <div class="min-h-screen bg-dark-900">
    <!-- Navigation -->
    <nav class="bg-dark-800 border-b border-white/10 px-6 py-4">
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <div class="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
            <QrCodeIcon class="w-6 h-6 text-white" />
          </div>
          <span class="text-xl font-bold text-gradient">Digital Menu AI</span>
        </div>
        <div class="flex items-center space-x-4">
          <span class="text-gray-300">Welcome, {{ user?.firstName }}</span>
          <button @click="logout" class="text-gray-400 hover:text-white transition-colors">
            <LogOutIcon class="w-5 h-5" />
          </button>
        </div>
      </div>
    </nav>

    <div class="bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 min-h-screen p-8">
      <div class="max-w-7xl mx-auto">
        <!-- Header -->
        <div class="mb-8">
          <div class="flex justify-between items-center">
            <div class="flex items-center space-x-4">
              <router-link to="/dashboard" class="text-gray-300 hover:text-white">
                <ArrowLeft class="w-6 h-6" />
              </router-link>
              <div>
                <h1 class="text-4xl font-bold text-white mb-2">Menu Management</h1>
                <p class="text-gray-300">Create and manage your restaurant menus independently from locations</p>
              </div>
            </div>
            <div class="flex gap-3">
              <button
                @click="navigateToCategories"
                class="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 flex items-center gap-2"
              >
                <Settings class="w-5 h-5" />
                Manage Categories
              </button>
              <button
                @click="showCreateMenuModal = true"
                class="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center gap-2"
              >
                <Plus class="w-5 h-5" />
                Create Menu
              </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
        <p class="text-gray-400 mt-4">Loading menus...</p>
      </div>

      <!-- No Menus State -->
      <div v-else-if="menus.length === 0" class="text-center py-12">
        <Menu class="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 class="text-xl font-semibold text-white mb-2">No Menus Yet</h3>
        <p class="text-gray-400 mb-6">Create your first menu to start building your restaurant offerings</p>
        <button
          @click="showCreateMenuModal = true"
          class="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center gap-2"
        >
          <Plus class="w-5 h-5" />
          Create First Menu
        </button>
      </div>

      <!-- Menus Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div
          v-for="menu in menus"
          :key="menu.id"
          class="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-200 cursor-pointer"
          :class="{ 'ring-2 ring-purple-500': selectedMenu?.id === menu.id }"
          @click="selectMenu(menu)"
        >
          <div class="flex justify-between items-start mb-4">
            <div>
              <h3 class="text-xl font-semibold text-white mb-1">{{ menu.name }}</h3>
              <p class="text-gray-300 text-sm">{{ menu.description }}</p>
            </div>
            <div class="flex gap-2">
              <button
                @click.stop="previewMenu(menu)"
                class="p-2 text-gray-300 hover:text-purple-400 hover:bg-white/20 rounded-lg transition-all duration-200"
                title="Preview Menu"
              >
                <Eye class="w-4 h-4" />
              </button>
              <button
                @click.stop="editMenu(menu)"
                class="p-2 text-gray-300 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200"
                title="Edit Menu"
              >
                <Edit2 class="w-4 h-4" />
              </button>
              <button
                @click.stop="deleteMenu(menu.id)"
                class="p-2 text-gray-300 hover:text-red-400 hover:bg-white/20 rounded-lg transition-all duration-200"
                title="Delete Menu"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
          <div class="flex justify-between items-center text-sm text-gray-400">
            <span>{{ getMenuItemCount(menu.id) }} items</span>
            <span>{{ formatDate(menu.createdAt) }}</span>
          </div>
        </div>
      </div>

      <!-- Selected Menu Details -->
      <div v-if="selectedMenu" class="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h2 class="text-2xl font-bold text-white">{{ selectedMenu.name }}</h2>
            <p class="text-gray-300">{{ selectedMenu.description }}</p>
          </div>
          <div class="flex gap-2">
            <button
              @click="previewMenu(selectedMenu)"
              class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center gap-2"
            >
              <Eye class="w-4 h-4" />
              Preview Menu
            </button>
            <button
              @click="showCreateItemModal = true"
              class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center gap-2"
            >
              <Plus class="w-4 h-4" />
              Add Item
            </button>
          </div>
        </div>

        <!-- Menu Items Loading -->
        <div v-if="loadingItems" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
          <p class="text-gray-400 mt-2">Loading menu items...</p>
        </div>

        <!-- No Items State -->
        <div v-else-if="menuItems.length === 0" class="text-center py-8">
          <UtensilsCrossed class="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-white mb-2">No Items in this Menu</h3>
          <p class="text-gray-400 mb-4">Add your first menu item to get started</p>
          <button
            @click="showCreateItemModal = true"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center gap-2 mx-auto"
          >
            <Plus class="w-4 h-4" />
            Add First Item
          </button>
        </div>

        <!-- Categories and Items -->
        <div v-else class="space-y-6">
          <div v-for="category in categorizedItems" :key="category.name" class="bg-white/5 rounded-lg p-4">
            <h3 class="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Tag class="w-5 h-5" />
              {{ category.name }}
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                v-for="item in category.items"
                :key="item.id"
                class="bg-white/10 rounded-lg p-4 border border-white/20 hover:bg-white/20 transition-all duration-200"
              >
                <!-- Item Image -->
                <div v-if="item.imageUrl" class="w-full h-32 rounded-lg overflow-hidden mb-3">
                  <img 
                    :src="item.imageUrl" 
                    :alt="item.name"
                    class="w-full h-full object-cover"
                    @error="hideImage"
                  />
                </div>
                
                <div class="flex justify-between items-start mb-2">
                  <h4 class="font-semibold text-white">{{ item.name }}</h4>
                  <div class="flex gap-1">
                    <button
                      @click="editMenuItem(item)"
                      class="p-1 text-gray-300 hover:text-white hover:bg-white/20 rounded transition-all duration-200"
                    >
                      <Edit2 class="w-3 h-3" />
                    </button>
                    <button
                      @click="deleteMenuItem(item.id)"
                      class="p-1 text-gray-300 hover:text-red-400 hover:bg-white/20 rounded transition-all duration-200"
                    >
                      <Trash2 class="w-3 h-3" />
                    </button>
                  </div>
                </div>
                
                <p class="text-gray-300 text-sm mb-3">{{ item.description }}</p>
                
                <!-- Additional Info -->
                <div class="space-y-2 mb-3">
                  <div v-if="item.preparationTime > 0" class="flex items-center text-gray-400 text-xs">
                    <Clock class="w-3 h-3 mr-1" />
                    {{ item.preparationTime }} mins
                  </div>
                  
                  <div v-if="item.allergens && item.allergens.length > 0" class="flex items-center text-yellow-400 text-xs">
                    <AlertTriangle class="w-3 h-3 mr-1" />
                    {{ item.allergens.join(', ') }}
                  </div>
                  
                  <div v-if="item.nutritionalInfo && Object.keys(item.nutritionalInfo).length > 0" class="text-gray-400 text-xs">
                    {{ formatNutritionalInfo(item.nutritionalInfo) }}
                  </div>
                </div>
                
                <div class="flex justify-between items-center">
                  <span class="text-green-400 font-semibold">{{ formatPrice(item.price, selectedMenu?.currency) }}</span>
                  <span :class="item.isAvailable ? 'text-green-400' : 'text-red-400'" class="text-xs">
                    {{ item.isAvailable ? 'Available' : 'Unavailable' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Menu Modal -->
      <div v-if="showCreateMenuModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
        <div class="bg-white/10 backdrop-blur-lg rounded-xl p-8 w-full max-w-md border border-white/20">
          <h3 class="text-2xl font-bold text-white mb-6">{{ editingMenu ? 'Edit Menu' : 'Create New Menu' }}</h3>
          <form @submit.prevent="saveMenu" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Menu Name</label>
              <input
                v-model="menuForm.name"
                type="text"
                required
                class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                placeholder="e.g., Lunch Menu, Dinner Menu"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                v-model="menuForm.description"
                rows="3"
                class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                placeholder="Brief description of this menu..."
              ></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Currency</label>
              <select
                v-model="menuForm.currency"
                class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
              >
                <option value="USD" class="bg-gray-800">US Dollar ($)</option>
                <option value="EUR" class="bg-gray-800">Euro (€)</option>
                <option value="GBP" class="bg-gray-800">British Pound (£)</option>
                <option value="RON" class="bg-gray-800">Romanian Leu (lei)</option>
                <option value="CAD" class="bg-gray-800">Canadian Dollar (C$)</option>
                <option value="AUD" class="bg-gray-800">Australian Dollar (A$)</option>
                <option value="JPY" class="bg-gray-800">Japanese Yen (¥)</option>
                <option value="CHF" class="bg-gray-800">Swiss Franc (CHF)</option>
                <option value="CNY" class="bg-gray-800">Chinese Yuan (¥)</option>
                <option value="INR" class="bg-gray-800">Indian Rupee (₹)</option>
                <option value="MXN" class="bg-gray-800">Mexican Peso ($)</option>
                <option value="BRL" class="bg-gray-800">Brazilian Real (R$)</option>
                <option value="KRW" class="bg-gray-800">South Korean Won (₩)</option>
                <option value="SGD" class="bg-gray-800">Singapore Dollar (S$)</option>
                <option value="HKD" class="bg-gray-800">Hong Kong Dollar (HK$)</option>
                <option value="NOK" class="bg-gray-800">Norwegian Krone (kr)</option>
                <option value="SEK" class="bg-gray-800">Swedish Krona (kr)</option>
                <option value="DKK" class="bg-gray-800">Danish Krone (kr)</option>
                <option value="NZD" class="bg-gray-800">New Zealand Dollar (NZ$)</option>
                <option value="ZAR" class="bg-gray-800">South African Rand (R)</option>
                <option value="PLN" class="bg-gray-800">Polish Złoty (zł)</option>
                <option value="CZK" class="bg-gray-800">Czech Koruna (Kč)</option>
                <option value="HUF" class="bg-gray-800">Hungarian Forint (Ft)</option>
                <option value="RUB" class="bg-gray-800">Russian Ruble (₽)</option>
              </select>
            </div>
            <div class="flex space-x-4 pt-4">
              <button type="submit" :disabled="!menuForm.name" class="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex-1">
                {{ editingMenu ? 'Update' : 'Create' }} Menu
              </button>
              <button type="button" @click="closeMenuModal" class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex-1">Cancel</button>
            </div>
          </form>
        </div>
      </div>

      <!-- Create Item Modal -->
      <div v-if="showCreateItemModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-6">
        <div class="bg-white/10 backdrop-blur-lg rounded-xl p-8 w-full max-w-3xl border border-white/20 max-h-[90vh] overflow-y-auto">
          <h3 class="text-2xl font-bold text-white mb-6">{{ editingItem ? 'Edit Menu Item' : 'Add New Menu Item' }}</h3>
          <form @submit.prevent="saveMenuItem" class="space-y-6">
            <!-- Basic Information -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Item Name *</label>
                <input
                  v-model="itemForm.name"
                  type="text"
                  required
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  placeholder="e.g., Grilled Chicken Sandwich"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Price *</label>
                <input
                  v-model.number="itemForm.price"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  placeholder="0.00"
                >
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                v-model="itemForm.description"
                rows="3"
                class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                placeholder="Describe the menu item..."
              ></textarea>
            </div>

            <!-- Category and Image -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  v-model="itemForm.category"
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="" class="bg-gray-800">Select a category</option>
                  <option 
                    v-for="category in categories" 
                    :key="category.id" 
                    :value="category.name"
                    class="bg-gray-800"
                  >
                    {{ category.name }}
                  </option>
                  <option value="custom" class="bg-gray-800">+ Custom Category</option>
                </select>
                
                <!-- Custom category input -->
                <input
                  v-if="itemForm.category === 'custom'"
                  v-model="customCategory"
                  type="text"
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 mt-2"
                  placeholder="Enter custom category name"
                  @blur="setCustomCategory"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
                <input
                  v-model="itemForm.imageUrl"
                  type="url"
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  placeholder="https://example.com/image.jpg"
                >
              </div>
            </div>

            <!-- Preparation Time -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Preparation Time (minutes)</label>
                <input
                  v-model.number="itemForm.preparationTime"
                  type="number"
                  min="0"
                  class="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                  placeholder="0"
                >
              </div>
            </div>

            <!-- Allergens -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Allergens</label>
              <div class="space-y-3">
                <!-- Common allergens checkboxes -->
                <div class="flex flex-wrap gap-2">
                  <div v-for="allergen in commonAllergens" :key="allergen" class="flex items-center">
                    <input
                      :id="'allergen-' + allergen"
                      v-model="itemForm.allergens"
                      :value="allergen"
                      type="checkbox"
                      class="rounded border-gray-600 bg-white/10 text-purple-500 focus:ring-purple-500"
                    >
                    <label :for="'allergen-' + allergen" class="ml-2 text-sm text-gray-300">{{ allergen }}</label>
                  </div>
                </div>
                
                <!-- Custom allergen input -->
                <div class="flex gap-2">
                  <input
                    v-model="customAllergen"
                    type="text"
                    class="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    placeholder="Add custom allergen"
                    @keyup.enter.prevent="addCustomAllergen"
                  >
                  <button
                    type="button"
                    @click="addCustomAllergen"
                    class="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                
                <!-- Selected allergens display (custom ones with remove option) -->
                <div v-if="customAllergensList.length > 0" class="flex flex-wrap gap-2">
                  <span class="text-sm text-gray-400">Custom allergens:</span>
                  <div v-for="allergen in customAllergensList" :key="allergen" class="flex items-center bg-purple-600/20 border border-purple-500/30 rounded-full px-3 py-1">
                    <span class="text-sm text-purple-300">{{ allergen }}</span>
                    <button
                      type="button"
                      @click="removeCustomAllergen(allergen)"
                      class="ml-2 text-purple-400 hover:text-red-400 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Nutritional Information -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">Nutritional Information</label>
              <div class="space-y-2">
                <div v-for="(value, key) in itemForm.nutritionalInfo" :key="key" class="flex gap-2">
                  <input
                    :value="key"
                    type="text"
                    class="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    :placeholder="key"
                    readonly
                  >
                  <input
                    v-model="itemForm.nutritionalInfo[key]"
                    type="text"
                    class="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    placeholder="Value"
                  >
                  <button
                    type="button"
                    @click="removeNutritionalInfo(key)"
                    class="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    ×
                  </button>
                </div>
                <div class="flex gap-2">
                  <input
                    v-model="newNutritionalKey"
                    type="text"
                    class="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    placeholder="e.g., Calories, Protein, etc."
                  >
                  <input
                    v-model="newNutritionalValue"
                    type="text"
                    class="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    placeholder="e.g., 350, 25g, etc."
                  >
                  <button
                    type="button"
                    @click="addNutritionalInfo"
                    class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <!-- Availability -->
            <div class="flex items-center">
              <input
                v-model="itemForm.isAvailable"
                type="checkbox"
                id="available"
                class="rounded border-gray-600 bg-white/10 text-purple-500 focus:ring-purple-500"
              >
              <label for="available" class="ml-3 text-sm text-gray-300">Available for ordering</label>
            </div>

            <div class="flex space-x-4 pt-4">
              <button type="submit" :disabled="!itemForm.name || itemForm.price < 0" class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex-1">
                {{ editingItem ? 'Update' : 'Add' }} Item
              </button>
              <button type="button" @click="closeItemModal" class="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex-1">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ArrowLeft, Plus, Menu, Edit2, Trash2, Tag, UtensilsCrossed, Eye, Settings, QrCode as QrCodeIcon, LogOut as LogOutIcon, Clock, AlertTriangle } from 'lucide-vue-next'
import api from '@/services/api'
import { useToast } from '@/composables/useToast'
import { formatPrice as formatCurrency } from '@/utils/currency'

export default {
  name: 'MenuManagement',
  components: {
    ArrowLeft,
    Plus,
    Menu,
    Edit2,
    Trash2,
    Tag,
    UtensilsCrossed,
    Eye,
    Settings,
    QrCodeIcon,
    LogOutIcon,
    Clock,
    AlertTriangle
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      loading: false,
      loadingItems: false,
      menus: [],
      menuItems: [],
      categories: [],
      selectedMenu: null,
      menuItemCounts: {}, // Store item counts for all menus
      user: null, // Add user data
      
      // Modal states
      showCreateMenuModal: false,
      showCreateItemModal: false,
      showCategoryModal: false,
      
      // Forms
      menuForm: {
        name: '',
        description: '',
        currency: 'USD'
      },
      itemForm: {
        name: '',
        description: '',
        price: 0,
        category: '',
        imageUrl: '',
        isAvailable: true,
        allergens: [],
        nutritionalInfo: {},
        preparationTime: 0
      },
      customCategory: '',
      
      // New fields for enhanced item form
      customAllergen: '',
      newNutritionalKey: '',
      newNutritionalValue: '',
      commonAllergens: [
        'Gluten', 'Dairy', 'Eggs', 'Fish', 'Shellfish', 'Tree Nuts', 
        'Peanuts', 'Soy', 'Sesame', 'Mustard', 'Celery', 'Lupin'
      ],
      
      // Editing states
      editingMenu: null,
      editingItem: null
    }
  },
  computed: {
    categorizedItems() {
      const categories = {}
      
      this.menuItems.forEach(item => {
        const categoryName = item.category || 'Uncategorized'
        if (!categories[categoryName]) {
          categories[categoryName] = {
            name: categoryName,
            items: []
          }
        }
        categories[categoryName].items.push(item)
      })
      
      return Object.values(categories)
    },
    
    customAllergensList() {
      // Return allergens that are not in the common allergens list
      return this.itemForm.allergens.filter(allergen => 
        !this.commonAllergens.includes(allergen)
      )
    }
  },
  async mounted() {
    // Load user data from localStorage
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        this.user = JSON.parse(userStr)
      } catch (error) {
        console.error('Failed to parse user data:', error)
        this.user = null
      }
    }

    await Promise.all([
      this.loadMenus(),
      this.loadCategories()
    ])
  },
  methods: {
    navigateToCategories() {
      this.$router.push('/category-management')
    },
    
    async loadMenus() {
      try {
        this.loading = true
        this.menus = await api.getMenus()
        await this.loadAllMenuItemCounts()
      } catch (error) {
        console.error('Error loading menus:', error)
        this.toast.error('Loading Failed', 'Failed to load menus: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    async loadAllMenuItemCounts() {
      try {
        for (const menu of this.menus) {
          const items = await api.getMenuItems(menu.id)
          this.menuItemCounts[menu.id] = items.length
        }
      } catch (error) {
        console.error('Error loading menu item counts:', error)
        // Don't show error for counts as it's not critical
      }
    },

    async loadCategories() {
      try {
        this.categories = await api.getCategories()
      } catch (error) {
        console.error('Error loading categories:', error)
        // Don't show alert for categories as it's not critical
      }
    },
    
    async selectMenu(menu) {
      this.selectedMenu = menu
      await this.loadMenuItems(menu.id)
    },
    
    async loadMenuItems(menuId) {
      try {
        this.loadingItems = true
        this.menuItems = await api.getMenuItems(menuId)
      } catch (error) {
        console.error('Error loading menu items:', error)
        this.toast.error('Loading Failed', 'Failed to load menu items: ' + error.message)
      } finally {
        this.loadingItems = false
      }
    },
    
    getMenuItemCount(menuId) {
      return this.menuItemCounts[menuId] || 0
    },

    previewMenu(menu) {
      // Create a preview URL for the menu using a temporary location ID
      // This allows viewing any menu regardless of whether it's active
      const previewUrl = `${window.location.origin}/preview/menu/${menu.id}`
      window.open(previewUrl, '_blank')
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString()
    },
    
    // Menu management
    editMenu(menu) {
      this.editingMenu = menu
      this.menuForm = {
        name: menu.name,
        description: menu.description,
        currency: menu.currency || 'USD'
      }
      this.showCreateMenuModal = true
    },
    
    async saveMenu() {
      try {
        if (this.editingMenu) {
          const updatedMenu = await api.updateMenu(this.editingMenu.id, this.menuForm)
          const index = this.menus.findIndex(m => m.id === this.editingMenu.id)
          this.menus[index] = updatedMenu
          if (this.selectedMenu?.id === this.editingMenu.id) {
            this.selectedMenu = updatedMenu
          }
          this.toast.success('Menu Updated', 'Menu updated successfully')
        } else {
          const newMenu = await api.createMenu(this.menuForm)
          this.menus.push(newMenu)
          this.toast.success('Menu Created', 'Menu created successfully')
        }
        this.closeMenuModal()
      } catch (error) {
        console.error('Error saving menu:', error)
        this.toast.error('Save Failed', 'Failed to save menu: ' + error.message)
      }
    },
    
    async deleteMenu(menuId) {
      if (!confirm('Are you sure you want to delete this menu? This action cannot be undone.')) {
        return
      }
      
      try {
        await api.deleteMenu(menuId)
        this.menus = this.menus.filter(m => m.id !== menuId)
        if (this.selectedMenu?.id === menuId) {
          this.selectedMenu = null
          this.menuItems = []
        }
        this.toast.success('Menu Deleted', 'Menu deleted successfully')
      } catch (error) {
        console.error('Error deleting menu:', error)
        this.toast.error('Delete Failed', 'Failed to delete menu: ' + error.message)
      }
    },
    
    closeMenuModal() {
      this.showCreateMenuModal = false
      this.editingMenu = null
      this.menuForm = { name: '', description: '', currency: 'USD' }
    },
    
    // Menu item management
    editMenuItem(item) {
      this.editingItem = item
      this.itemForm = {
        name: item.name,
        description: item.description,
        price: item.price,
        category: item.category,
        imageUrl: item.imageUrl || '',
        isAvailable: item.isAvailable,
        allergens: Array.isArray(item.allergens) ? [...item.allergens] : [],
        nutritionalInfo: item.nutritionalInfo ? { ...item.nutritionalInfo } : {},
        preparationTime: item.preparationTime || 0
      }
      this.showCreateItemModal = true
    },
    
    async saveMenuItem() {
      try {
        if (this.editingItem) {
          const updatedItem = await api.updateMenuItem(this.selectedMenu.id, this.editingItem.id, this.itemForm)
          const index = this.menuItems.findIndex(i => i.id === this.editingItem.id)
          this.menuItems[index] = updatedItem
          this.toast.success('Item Updated', 'Menu item updated successfully')
        } else {
          const newItem = await api.createMenuItem(this.selectedMenu.id, this.itemForm)
          this.menuItems.push(newItem)
          this.menuItemCounts[this.selectedMenu.id] = (this.menuItemCounts[this.selectedMenu.id] || 0) + 1
          this.toast.success('Item Added', 'Menu item added successfully')
        }
        this.closeItemModal()
      } catch (error) {
        console.error('Error saving menu item:', error)
        this.toast.error('Save Failed', 'Failed to save menu item: ' + error.message)
      }
    },
    
    async deleteMenuItem(itemId) {
      if (!confirm('Are you sure you want to delete this menu item?')) {
        return
      }
      
      try {
        await api.deleteMenuItem(this.selectedMenu.id, itemId)
        this.menuItems = this.menuItems.filter(i => i.id !== itemId)
        this.menuItemCounts[this.selectedMenu.id] = Math.max(0, (this.menuItemCounts[this.selectedMenu.id] || 0) - 1)
        this.toast.success('Item Deleted', 'Menu item deleted successfully')
      } catch (error) {
        console.error('Error deleting menu item:', error)
        this.toast.error('Delete Failed', 'Failed to delete menu item: ' + error.message)
      }
    },
    
    closeItemModal() {
      this.showCreateItemModal = false
      this.editingItem = null
      this.itemForm = {
        name: '',
        description: '',
        price: 0,
        category: '',
        imageUrl: '',
        isAvailable: true,
        allergens: [],
        nutritionalInfo: {},
        preparationTime: 0
      }
      this.customCategory = ''
      this.customAllergen = ''
      this.newNutritionalKey = ''
      this.newNutritionalValue = ''
    },

    setCustomCategory() {
      if (this.customCategory.trim()) {
        this.itemForm.category = this.customCategory.trim()
      }
    },

    addCustomAllergen() {
      if (this.customAllergen.trim() && !this.itemForm.allergens.includes(this.customAllergen.trim())) {
        this.itemForm.allergens.push(this.customAllergen.trim())
        this.customAllergen = ''
      }
    },

    removeCustomAllergen(allergen) {
      const index = this.itemForm.allergens.indexOf(allergen)
      if (index > -1) {
        this.itemForm.allergens.splice(index, 1)
      }
    },

    addNutritionalInfo() {
      if (this.newNutritionalKey.trim() && this.newNutritionalValue.trim()) {
        // Use Vue.set or reactive assignment to ensure Vue detects the change
        const key = this.newNutritionalKey.trim()
        const value = this.newNutritionalValue.trim()
        
        // Create a new object to trigger reactivity
        this.itemForm.nutritionalInfo = {
          ...this.itemForm.nutritionalInfo,
          [key]: value
        }
        
        this.newNutritionalKey = ''
        this.newNutritionalValue = ''
      }
    },

    removeNutritionalInfo(key) {
      // Create a new object without the key to trigger reactivity
      const newInfo = { ...this.itemForm.nutritionalInfo }
      delete newInfo[key]
      this.itemForm.nutritionalInfo = newInfo
    },

    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    },

    hideImage(event) {
      event.target.style.display = 'none'
    },

    formatPrice(price, currency = null) {
      // Use passed currency or fallback to selected menu currency, default to USD if not available
      const currencyCode = currency || this.selectedMenu?.currency || 'USD'
      return formatCurrency(price, currencyCode)
    },

    formatNutritionalInfo(nutritionalInfo) {
      if (typeof nutritionalInfo === 'object' && nutritionalInfo !== null) {
        return Object.entries(nutritionalInfo)
          .map(([key, value]) => `${key}: ${value}`)
          .join(' | ')
      }
      return nutritionalInfo || ''
    }
  }
}
</script>
